import { useState, useEffect } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuthContext } from "@/contexts/AuthContext";
import { useCompanyApi, CompanyDashboardStats, CompanyUser, CompanyUsageStats } from "@/hooks/useCompanyApi";
import { Users, Activity, BarChart3, Calendar, Loader2, TrendingUp, User, Circle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";

const COLORS = ['hsl(210, 60%, 55%)', 'hsl(160, 50%, 50%)', 'hsl(280, 45%, 60%)'];

export default function CompanyDashboard() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const { user } = useAuthContext();
  const { getDashboardStats, listUsers, getUsageStats } = useCompanyApi();
  
  const [isLoading, setIsLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(false);
  const [stats, setStats] = useState<CompanyDashboardStats | null>(null);
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [usageStats, setUsageStats] = useState<CompanyUsageStats | null>(null);
  const [period, setPeriod] = useState<'1d' | '7d' | '30d' | '90d' | 'all'>('30d');

  const periodToStartDate = (p: typeof period): string | undefined => {
    if (p === 'all') return undefined;
    const days = p === '1d' ? 1 : p === '7d' ? 7 : p === '30d' ? 30 : 90;
    return new Date(Date.now() - days * 86400000).toISOString();
  };

  useEffect(() => {
    async function fetchInitial() {
      setIsLoading(true);
      try {
        const [statsRes, usersRes] = await Promise.all([
          getDashboardStats(),
          listUsers(),
        ]);

        if (statsRes.success && statsRes.data) {
          setStats(statsRes.data);
        } else if (!statsRes.success) {
          toast({
            title: tc('error'),
            description: statsRes.error || t('companyDashboard.errorFetchStats'),
            variant: "destructive",
          });
        }

        if (usersRes.success && usersRes.data) {
          setUsers(usersRes.data);
        }
      } catch (error) {
        console.error("Error fetching company data:", error);
        toast({
          title: tc('error'),
          description: t('companyDashboard.errorFetchData'),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }

    fetchInitial();
  }, [getDashboardStats, listUsers]);

  useEffect(() => {
    async function fetchUsage() {
      setUsageLoading(true);
      try {
        const start_date = periodToStartDate(period);
        const usageRes = await getUsageStats(start_date ? { start_date } : {});
        if (usageRes.success && usageRes.data) {
          setUsageStats(usageRes.data);
        }
      } catch (error) {
        console.error("Error fetching usage stats:", error);
      } finally {
        setUsageLoading(false);
      }
    }
    fetchUsage();
  }, [period, getUsageStats]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUsageTypeLabel = (type: string) => {
    switch (type) {
      case 'linkedin_profile': return t('companyDashboard.usageTypes.linkedin_profile');
      case 'message_generated': return t('companyDashboard.usageTypes.message_generated');
      case 'audio_generated': return t('companyDashboard.usageTypes.audio_generated');
      default: return type;
    }
  };

  const pieData = usageStats ? [
    { name: t('stats.linkedinProfiles'), value: usageStats.totals.linkedin },
    { name: t('stats.messages'), value: usageStats.totals.messages },
    { name: t('stats.audio'), value: usageStats.totals.audio },
  ].filter(d => d.value > 0) : [];

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {stats?.company_name || user?.company_name || tc('noData')}
          </h1>
          <p className="text-muted-foreground">
            {t('companyDashboard.subtitle')}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('companyDashboard.users')}</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.users_count || 0}</div>
              <p className="text-xs text-muted-foreground">
                {t('companyDashboard.activeUsers')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('companyDashboard.usageToday')}</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.today_usage || 0}</div>
              <p className="text-xs text-muted-foreground">
                {t('companyDashboard.operationsToday')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('companyDashboard.thisWeek')}</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.week_usage || 0}</div>
              <p className="text-xs text-muted-foreground">
                {t('companyDashboard.operationsThisWeek')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('companyDashboard.thisMonth')}</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.month_usage || 0}</div>
              <p className="text-xs text-muted-foreground">
                {t('companyDashboard.operationsThisMonth')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">{t('companyDashboard.overview')}</TabsTrigger>
            <TabsTrigger value="users">{t('companyDashboard.usersTab')}</TabsTrigger>
            <TabsTrigger value="stats">{t('companyDashboard.statsTab')}</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('companyDashboard.recentActivity')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.recent_activity && stats.recent_activity.length > 0 ? (
                    <div className="space-y-3">
                      {stats.recent_activity.slice(0, 5).map((activity) => (
                        <div key={activity.id} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {getUsageTypeLabel(activity.usage_type)}
                            </Badge>
                            <span className="text-muted-foreground">{activity.user_email}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(activity.created_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {t('companyDashboard.noRecentActivity')}
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('companyDashboard.usageDistribution')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={35}
                          outerRadius={65}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent, x, y }) => (
                            <text 
                              x={x} 
                              y={y} 
                              fill="hsl(var(--foreground))" 
                              fontSize={11} 
                              textAnchor="middle"
                              dominantBaseline="middle"
                            >
                              {`${name} ${(percent * 100).toFixed(0)}%`}
                            </text>
                          )}
                          labelLine={{ stroke: 'hsl(var(--muted-foreground))' }}
                        >
                          {pieData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: 'white' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[200px]">
                      <p className="text-sm text-muted-foreground">{tc('noData')}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>{t('companyDashboard.companyUsers')}</CardTitle>
              </CardHeader>
              <CardContent>
                {users.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{tc('email')}</TableHead>
                        <TableHead>{tc('role')}</TableHead>
                        <TableHead>{tc('status')}</TableHead>
                        <TableHead>{t('users.session')}</TableHead>
                        <TableHead>{t('companyDashboard.createdAt')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              {user.email}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.role === 'company_admin' ? 'default' : 'secondary'}>
                              {t(`companyDashboard.roleLabels.${user.role}`, { defaultValue: user.role })}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.is_active ? 'outline' : 'destructive'}>
                              {user.is_active ? tc('active') : tc('inactive')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Circle 
                                className={`h-2 w-2 ${user.has_active_session ? 'fill-green-500 text-green-500' : 'fill-muted text-muted'}`} 
                              />
                              <span className="text-xs text-muted-foreground">
                                {user.has_active_session ? tc('online') : tc('offline')}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(user.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t('companyDashboard.noCompanyUsers')}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats" className="space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Select value={period} onValueChange={(v) => setPeriod(v as typeof period)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1d">{t('stats.today')}</SelectItem>
                    <SelectItem value="7d">{t('stats.7days')}</SelectItem>
                    <SelectItem value="30d">{t('stats.30days')}</SelectItem>
                    <SelectItem value="90d">{t('stats.90days')}</SelectItem>
                    <SelectItem value="all">{t('stats.all')}</SelectItem>
                  </SelectContent>
                </Select>
                {usageLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
              {usageStats?.truncated && (
                <p className="text-xs text-muted-foreground">{t('stats.truncatedNote')}</p>
              )}
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  {t('companyDashboard.usageChart')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {usageStats?.chart_data && usageStats.chart_data.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={usageStats.chart_data}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return `${date.getDate()}.${date.getMonth() + 1}`;
                        }}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        labelFormatter={(value) => new Date(value).toLocaleDateString('pl-PL')}
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="linkedin" 
                        name={t('stats.linkedinProfiles')} 
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="messages" 
                        name={t('stats.messages')} 
                        stroke="hsl(var(--chart-2))" 
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="audio" 
                        name={t('stats.audio')} 
                        stroke="hsl(var(--chart-3))" 
                        strokeWidth={2}
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px]">
                    <p className="text-sm text-muted-foreground">{t('companyDashboard.noChartData')}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('companyDashboard.usageByUser')}</CardTitle>
              </CardHeader>
              <CardContent>
                {usageStats?.by_user && usageStats.by_user.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{tc('email')}</TableHead>
                        <TableHead className="text-right">{t('stats.linkedinProfiles')}</TableHead>
                        <TableHead className="text-right">{t('stats.messages')}</TableHead>
                        <TableHead className="text-right">{t('stats.audio')}</TableHead>
                        <TableHead className="text-right">{tc('total')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usageStats.by_user.map((user) => (
                        <TableRow key={user.user_id}>
                          <TableCell className="font-medium">{user.email}</TableCell>
                          <TableCell className="text-right">{user.linkedin}</TableCell>
                          <TableCell className="text-right">{user.messages}</TableCell>
                          <TableCell className="text-right">{user.audio}</TableCell>
                          <TableCell className="text-right font-bold">{user.total}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t('companyDashboard.noUsageData')}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
